import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loading } from 'framework/ui/mui-builder';
import {
  OneStopCreateForm,
  parseOneStopQueryParams,
  useOneStopRequestId,
  useOneStopRequestsUrl,
  useOneStopService,
  useOneStopConfiguration,
  OneStopModalDecorator,
  OneStopRequestLayout,
  Request,
} from 'domain/shared/oneStop';
import { FormBuilder, type FormInputs, FormInputType } from 'domain/shared/core/FormBuilder';
import { useSearchParameters } from 'framework/routing';
import OneStopCreatePageLayout from 'domain/gpo/oneStop/Create/OneStopCreatePageLayout';
import CustomRequestFormWrapper from 'domain/gpo/oneStop/CustomRequestForm/CustomRequestFormWrapper';

const OneStopCreatePage = () => {
  const [request, setRequest] = useState<Nullable<Request>>(null);
  const [formConfiguration, setFormConfiguration] = useState<Nullable<FormInputs>>(null);
  const [isLoading, setLoading] = useState<Boolean>(true);
  const { search } = useLocation();
  const navigate = useNavigate();
  const id = useOneStopRequestId()!;
  const initialDataFromSearch = useSearchParameters();
  const { getListUrl } = useOneStopRequestsUrl();
  const { getById, sendRequest, getFieldSourcesForHtmlFields, getFormattedData } = useOneStopService();

  const prefillData = useMemo(() => parseOneStopQueryParams(new URLSearchParams(search)), [search]);

  const { getRequestConfiguration, getFormConfiguration, getValidationSchema } = useOneStopConfiguration({
    getById,
    id,
    prefillData,
  });

  useEffect(() => {
    Promise.all([
      getRequestConfiguration().then(data => setRequest(data)),
      getFormConfiguration().then(data => setFormConfiguration(data)),
    ])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const initialData = useMemo(() => {
    const fields = request && request.fields ? request.fields : [];

    return {
      ...Object.fromEntries(
        fields.map(field =>
          field.type === FormInputType.CHECKBOX
            ? [field.id, field.defaultValue === 'true']
            : [field.id, field.defaultValue],
        ),
      ),
      ...initialDataFromSearch,
      ...prefillData,
    };
  }, [request, initialDataFromSearch, prefillData]);

  if (isLoading) {
    return <Loading />;
  }

  if (!request || !formConfiguration) {
    return <OneStopCreatePageLayout />;
  }

  const isCustomRequestForm = !request.fields;

  if (isCustomRequestForm) {
    return (
      <OneStopCreatePageLayout>
        <CustomRequestFormWrapper request={request} />
      </OneStopCreatePageLayout>
    );
  }

  const handleCancelClick = () => {
    if (initialData.from) {
      navigate(initialData.from);
    } else {
      navigate(getListUrl());
    }
  };

  const requestContext = { type: request?.type };

  const htmlFieldSources = getFieldSourcesForHtmlFields(formConfiguration);

  return (
    <OneStopModalDecorator requestContext={requestContext}>
      <OneStopCreateForm
        requestId={id}
        initialData={initialData}
        getValidationSchemaFunc={getValidationSchema}
        submitFormFunc={(data: any) => {
          const formattedData = getFormattedData(data, htmlFieldSources);
          return sendRequest(id, formattedData);
        }}
      >
        <OneStopCreatePageLayout id={request.id}>
          <OneStopRequestLayout request={request} handleCancelClick={handleCancelClick} isFixedButtons={false}>
            <FormBuilder formInputs={formConfiguration} />
          </OneStopRequestLayout>
        </OneStopCreatePageLayout>
      </OneStopCreateForm>
    </OneStopModalDecorator>
  );
};

export default OneStopCreatePage;
